import moment from '@/config/moment'
import { PageLayout } from '@/layouts/page-layout'
import { Box } from '@/shared/components/box'
import { ExpandableTable } from '@/shared/components/expandable-table'
import { useGetDashboardYearlyAttachmentQuery } from '@/store/services'
import { Alert, Chip, Typography } from '@material-tailwind/react'
import { Suspense } from 'react'
import { useSelector } from 'react-redux'
import { Await, useLoaderData, useSearchParams } from 'react-router-dom'
import { DashboardFilter, PeriodFilter } from './components'
import { DashboardCardSkeleton } from './components/dashboard-card-skeleton'
import { DashboardCards } from './dashboard-cards'
import {
  AccreditationHorizontalBar,
  AccreditationTotalBox,
  DetailComplianceTable,
  DetailRiskTable,
  MonthlyAttachmentChart,
  MonthlyComplianceBox,
  MonthlyRiskBox
} from './items'
import openingDates from './temporal-opening-dates.json'
import { PageLayoutError } from '@/layouts'

export const Dashboard = () => {
  const [searchParams] = useSearchParams()
  const { current: user } = useSelector((state) => state.user)
  const { monthlyRisk, dashboardItems, accreditation, monthlyAttachments } = useLoaderData()

  const defaultQueryKeys = ['company_id', 'contract_id', 'office_id', 'user_id', 'submanagment_id', 'managment_id']
  const selectedSearchParams = new URLSearchParams(
    [...searchParams.entries()].filter(([key]) => defaultQueryKeys.includes(key))
  )
  const { data: yearlyValues = {} } = useGetDashboardYearlyAttachmentQuery(
    `?${selectedSearchParams.toString()}`,
    { refetchOnMountOrArgChange: true }
  )

  const currentDate = searchParams.get('period') ? moment(searchParams.get('period'), 'DD-MM-YYYY').format('MMMM Y') : moment().format('MMMM Y')

  return (
    user?.id && (
      <PageLayout
        title='Dashboard'
        className='flex flex-col gap-6 h-full'
        actions={[
          <div key='actions' className='flex gap-2 items-center justify-end'>
            <Chip
              className='bg-blue-50 text-blue-600 w-fit h-fit'
              variant='ghost'
              value={currentDate}
            />
            <PeriodFilter />
            <DashboardFilter />
          </div>
        ]}
      >
        <Alert className='bg-gray-50 border text-gray-600' variant='ghost'>
          <span>
            ¡Bienvenido! Si necesitas asistencia, contáctanos a:{' '}
            <a className='text-blue-600' href='mailto:contacto@apsplus.cl'>
              contacto@apsplus.cl
            </a>
          </span>
        </Alert>
        <div className='flex flex-col'>
          <div className='flex flex-col mb-4' >
            <Typography variant='h5'>
              Información rápida
            </Typography>

            <Suspense fallback={
              <div className='grid sm:grid-cols-2 md:grid-cols-3 gap-4 lg:grid-cols-6'>
                <DashboardCardSkeleton />
                <DashboardCardSkeleton />
                <DashboardCardSkeleton />
                <DashboardCardSkeleton />
                <DashboardCardSkeleton />
                <DashboardCardSkeleton />
              </div>
            }
            >
              <Await resolve={dashboardItems} errorElement={<PageLayoutError hideButton />}>
                {(data) => <DashboardCards data={data} />}
              </Await>
            </Suspense>
            <div className='col-span-full'></div>
          </div>

          <div className='grid grid-cols-12 gap-4'>
            <Typography className='col-span-full' variant='h5'>
              Gestión documental
            </Typography>
            <div className='sm:col-span-full md:col-span-full lg:col-span-5 flex flex-col gap-4'>
              <div
                className='grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-4 h-full'
              >
                <Suspense fallback={<DashboardCardSkeleton />}>
                  <Await resolve={accreditation} errorElement={<PageLayoutError hideButton />}>
                    {(data) => <AccreditationTotalBox accreditation={data} />}
                  </Await>
                </Suspense>

                <Suspense fallback={<DashboardCardSkeleton />}>
                  <Await resolve={monthlyAttachments} errorElement={<PageLayoutError hideButton />}>
                    {(data) => <MonthlyComplianceBox monthlyAttachments={data} />}
                  </Await>
                </Suspense>
                <Suspense fallback={<DashboardCardSkeleton />} >
                  <Await resolve={monthlyRisk} errorElement={<PageLayoutError hideButton />}>
                    {(data) => <MonthlyRiskBox data={data} />}
                  </Await>
                </Suspense>

                <Box className='col-span-full flex flex-col gap-4'>
                  <Typography className='text-black font-medium'>
                    Acreditación
                  </Typography>

                  <Suspense fallback={<AccreditationHorizontalBar accreditation={{}} />}>
                    <Await resolve={accreditation} errorElement={<PageLayoutError hideButton />}>
                      {(data) => <AccreditationHorizontalBar accreditation={data} />}
                    </Await>
                  </Suspense>
                </Box>
              </div>
            </div>

            <Box className=' sm:col-span-full md:col-span-full lg:col-span-7 flex flex-col gap-4'>
              <Typography className='text-black font-medium'>
                Histórico de cumplimiento
              </Typography>
              <MonthlyAttachmentChart values={yearlyValues} />
            </Box>

            <Box className='col-span-full flex flex-col gap-6'>
              <div>
                <Typography variant='paragraph' className='font-medium'>
                  Detalle de índice de cumplimiento
                </Typography>
              </div>
              <Suspense fallback={<DetailComplianceTable data={{}} />}>
                <Await resolve={monthlyAttachments} errorElement={<PageLayoutError hideButton />}>
                  {(data) => <DetailComplianceTable data={data} />}
                </Await>
              </Suspense>
            </Box>

            <Box className='col-span-full flex flex-col gap-6'>
              <div>
                <Typography variant='paragraph' className='font-medium'>
                  Detalle de índice de riesgo
                </Typography>
              </div>
              <Suspense fallback={<DetailRiskTable />}>
                <Await resolve={monthlyRisk} errorElement={<PageLayoutError hideButton />} >
                  {(data) => <DetailRiskTable />}
                </Await>
              </Suspense>
            </Box>

            <Box className='col-span-full flex flex-col gap-6'>
              <div>
                <Typography variant='paragraph' className='font-medium'>
                  Fechas de apertura e informes
                </Typography>
              </div>

              <ExpandableTable
                data={openingDates}
                columns={[
                  { key: 'startDate', label: 'Apertura' },
                  { key: 'endDate', label: 'Cierre' },
                  { key: 'reportDate', label: 'Informe final' },
                  { key: 'controlledPeriod', label: 'Periodo controlado' }
                ]}
              />
            </Box>
          </div>
        </div>
      </PageLayout>
    )
  )
}
